/**
 * @file woundHazardModule.js
 * @version 1.0.0
 * @date October 10, 2026
 *
 * @description
 * Wound-type hazard model (docs/documentation/SPEC_WOUND_HAZARD.md): cumulative death probability by wound region as a
 * function of time since injury, the care in place, and (for DOW) the times of arrival and surgery.
 * PURE functions: no side effects, no module state. Parameters come from constants/woundHazardParameters.js.
 *
 * Missing required inputs THROW (APP_RULES 6.1). A care flag that is not set means "not in place".
 *
 * DOW definition (Mabry 2015; Eastridge 2011): death AFTER reaching a facility.
 *   prehospital deaths = M(tArrival)
 *   DOW                = ( M(tSurgery) - M(tArrival) ) / ( 1 - M(tArrival) )
 * so a longer evacuation moves deaths from DOW to prehospital instead of only raising one number.
 *
 * Not wired into the live calculator yet (plan step 5). Nothing here is used by casualtyCalculator.
 */
import {
  WOUND_HAZARD_COMPONENTS,
  WOUND_HAZARD_REGIONS,
  MULTI_REGION_PARAMETERS,
  T2_TIME_TRANSFORM,
  DOW_ARRIVAL
} from '../../constants/woundHazardParameters.js';

const WHERE = 'woundHazardModule';

const requireTimeHours = (value, name) => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0) {
    throw new Error(`${WHERE}: ${name} must be a number of hours >= 0 (got: ${value})`);
  }
  return value;
};

/**
 * Validate the care input.
 * @param {{controlTimeMin: number, trainingScale: number, flags: Object<string, boolean>}} care
 *   trainingScale: tourniquet success relative to standard first-aid training (1 = standard).
 *   controlTimeMin: minutes until bleeding or airway is controlled; Infinity = never controlled.
 *   flags: care flags in place (see CARE_FLAGS); a missing key means not in place.
 */
const requireCare = (care) => {
  if (!care || typeof care !== 'object') throw new Error(`${WHERE}: care input is required`);
  if (typeof care.controlTimeMin !== 'number' || Number.isNaN(care.controlTimeMin) || care.controlTimeMin < 0) {
    throw new Error(`${WHERE}: care.controlTimeMin must be a number of minutes >= 0 or Infinity (got: ${care.controlTimeMin})`);
  }
  if (!care.flags || typeof care.flags !== 'object') throw new Error(`${WHERE}: care.flags object is required`);
  if (typeof care.trainingScale !== 'number' || Number.isNaN(care.trainingScale) || care.trainingScale <= 0) {
    throw new Error(`${WHERE}: care.trainingScale must be a number > 0 (1 = standard first-aid training; got: ${care.trainingScale})`);
  }
  return care;
};

const careMultiplier = (factors, flags) =>
  Object.keys(factors).reduce((m, key) => (flags[key] === true ? m * factors[key] : m), 1);

const ex = (x) => 1 - Math.exp(-x);

/** Death probability contributed by one component at tHours after injury. */
export const componentMortality = (component, tHours, care) => {
  requireTimeHours(tHours, 'tHours');
  requireCare(care);
  const m = careMultiplier(component.factors, care.flags);
  const tMin = tHours * 60;
  switch (component.kind) {
    case 'saturating':
      return component.p * m * ex(tMin / component.tauMin);
    case 'controllable': {
      const tc = Math.max(care.controlTimeMin, component.minControlMin);
      const controlled = ex(Math.min(tMin, tc) / component.tauMin);
      const uncontrolled = ex(tMin / component.tauMin);
      const eff = component.scaleEfficacyWithTraining ? Math.min(1, component.eff * care.trainingScale) : component.eff;
      return component.p * m * (eff * controlled + (1 - eff) * uncontrolled);
    }
    case 'weibull': {
      // R2-24d: the late deaths (infection, sepsis, ischemia: what surgery prevents) have no data for a casualty who waits many hours; the
      // Monte Carlo varies their size and their timing (care.tailScale, care.tailTiming; 1 = the central values)
      const tailScale = typeof care.tailScale === 'number' ? care.tailScale : 1;
      const tailTiming = typeof care.tailTiming === 'number' ? care.tailTiming : 1;
      return Math.min(1, component.p * tailScale) * m * ex(Math.pow(Math.max(0, tHours - component.t0H) / (component.etaH * tailTiming), component.k));
    }
    default:
      throw new Error(`${WHERE}: unknown component kind '${component.kind}' (${component.id})`);
  }
};

const combine = (probabilities) => 1 - probabilities.reduce((s, p) => s * (1 - p), 1);

/** Share of each region that can pair in a multi-region wound, read from the wound distribution. */
const pairWeights = (woundDistribution) => {
  const regions = MULTI_REGION_PARAMETERS.pairRegions;
  const raw = regions.map((r) => {
    const w = woundDistribution?.[r];
    if (typeof w !== 'number' || Number.isNaN(w) || w < 0) {
      throw new Error(`${WHERE}: woundDistribution.${r} must be a number >= 0 (got: ${w})`);
    }
    return w;
  });
  const total = raw.reduce((a, b) => a + b, 0);
  if (total <= 0) throw new Error(`${WHERE}: woundDistribution has no weight in the multi-region pair regions`);
  return regions.map((r, i) => ({ region: r, weight: raw[i] / total }));
};

const multiRegionMortality = (tHours, care, woundDistribution) => {
  const weights = pairWeights(woundDistribution);
  const hazard = (region) => -Math.log(1 - regionMortality(region, tHours, care, woundDistribution));
  const hazards = weights.map((w) => hazard(w.region));
  let num = 0;
  let den = 0;
  for (let i = 0; i < weights.length; i += 1) {
    for (let j = i; j < weights.length; j += 1) {
      const w = weights[i].weight * weights[j].weight * (i === j ? 1 : 2);
      const h = hazards[i] + (i === j ? 0 : hazards[j]);
      num += w * (1 - Math.exp(-MULTI_REGION_PARAMETERS.synergy * h));
      den += w;
    }
  }
  return num / den;
};

/**
 * Cumulative death probability of a T1-like casualty with a wound in `region`, tHours after injury.
 * @param {string} region one of WOUND_HAZARD_REGIONS
 * @param {number} tHours hours since injury
 * @param {{controlTimeMin:number, flags:Object}} care
 * @param {Object} [woundDistribution] required for region 'multi' (pair mix)
 */
export const regionMortality = (region, tHours, care, woundDistribution) => {
  if (!WOUND_HAZARD_REGIONS.includes(region)) {
    throw new Error(`${WHERE}: unknown wound region '${region}' (valid: ${WOUND_HAZARD_REGIONS.join(', ')})`);
  }
  requireTimeHours(tHours, 'tHours');
  requireCare(care);
  if (region === 'multi') return multiRegionMortality(tHours, care, woundDistribution);
  return combine(WOUND_HAZARD_COMPONENTS[region].map((c) => componentMortality(c, tHours, care)));
};

/** T2 casualties: stable for a window, then the T1 curve scaled by severity (time-based, not a fixed share of T1). */
export const regionMortalityByTier = (tier, region, tHours, care, woundDistribution) => {
  if (tier === 'T1') return regionMortality(region, tHours, care, woundDistribution);
  if (tier === 'T2') {
    return T2_TIME_TRANSFORM.severityShare *
      regionMortality(region, Math.max(0, tHours - T2_TIME_TRANSFORM.stableWindowHours), care, woundDistribution);
  }
  throw new Error(`${WHERE}: tier must be 'T1' or 'T2' (got: ${tier})`);
};

/** Deaths between injury and arrival, as a share of casualties with this wound (not DOW). */
export const prehospitalDeaths = (region, arrivalHours, care, woundDistribution, tier = 'T1') =>
  regionMortalityByTier(tier, region, requireTimeHours(arrivalHours, 'arrivalHours'), care, woundDistribution);

/** Share of casualties arriving alive who die before surgery. */
export const dowBetween = (region, arrivalHours, surgeryHours, care, woundDistribution, tier = 'T1') => {
  requireTimeHours(arrivalHours, 'arrivalHours');
  requireTimeHours(surgeryHours, 'surgeryHours');
  if (surgeryHours < arrivalHours) {
    throw new Error(`${WHERE}: surgeryHours (${surgeryHours}) cannot be before arrivalHours (${arrivalHours})`);
  }
  const atArrival = regionMortalityByTier(tier, region, arrivalHours, care, woundDistribution);
  const atSurgery = regionMortalityByTier(tier, region, surgeryHours, care, woundDistribution);
  if (atArrival >= 1) return 0;
  return (atSurgery - atArrival) / (1 - atArrival);
};

/**
 * Deaths between arrival and surgery as a share of ALL casualties with this wound (M(ts) - M(ta)). This is the share
 * that applies to the wounded count; `dowBetween` is the same death count as a share of those who ARRIVED alive
 * (the published DOW definition), larger by 1 / (1 - prehospital share).
 */
export const dowShareOfCasualties = (region, arrivalHours, surgeryHours, care, woundDistribution, tier = 'T1') => {
  requireTimeHours(arrivalHours, 'arrivalHours');
  requireTimeHours(surgeryHours, 'surgeryHours');
  if (surgeryHours < arrivalHours) {
    throw new Error(`${WHERE}: surgeryHours (${surgeryHours}) cannot be before arrivalHours (${arrivalHours})`);
  }
  return regionMortalityByTier(tier, region, surgeryHours, care, woundDistribution) -
    regionMortalityByTier(tier, region, arrivalHours, care, woundDistribution);
};

/**
 * Arrival time in hours at DOW_ARRIVAL.facility, read from the segments of the cumulative
 * time-to-surgery analysis ({ to, time_min } in order). Throws if the facility is not on the route.
 */
export const arrivalHoursFromSegments = (segments, facility = DOW_ARRIVAL.facility) => {
  if (!Array.isArray(segments) || segments.length === 0) throw new Error(`${WHERE}: segments array is required`);
  let cumulative = 0;
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    if (typeof seg.time_min !== 'number' || Number.isNaN(seg.time_min)) {
      throw new Error(`${WHERE}: segment ${i} (${seg.from}->${seg.to}) has no time_min`);
    }
    cumulative += seg.time_min;
    if (seg.to === facility) return cumulative / 60;
  }
  throw new Error(`${WHERE}: facility '${facility}' is not on the evacuation route (${segments.map((s) => s.to).join(', ')})`);
};

const WOUND_REGIONS_IN_DISTRIBUTION = ['extremity', 'thorax', 'abdomen', 'head_neck', 'burns', 'multi'];

/**
 * Base DOW rate from wounds (replaces the flat per-region rates when the model is switched on).
 * Same structure as the existing refinement: T1 and T2 terms by wound region, T3 and T4 unchanged.
 *
 * @param {Object} p
 * @param {Object} p.woundDistribution share of wounded by region (sum about 1)
 * @param {{t1:number,t2:number,t3:number,t4:number}} p.triageMix shares of WIA by triage category
 * @param {number} p.t3Mortality @param {number} p.t4Mortality mortality rates of T3 and T4
 * @param {number} p.arrivalHours @param {number} p.surgeryHours
 * @param {number} [p.surgeryHoursT1] time to surgery of T1 when it differs (priority queue: T1 is operated first); default = surgeryHours
 * @param {number} [p.t2ExtraWaitHours] extra wait of T2 (and below) compared with T1 before the first movement (evacuation queue, R2-29: urgent are evacuated first); default 0
 * @param {{controlTimeMin:number, flags:Object}} p.care
 * @returns {{dowRate:number, prehospitalRate:number, dowRateOfArrivals:number, byRegion:Object}}
 *   dowRate and prehospitalRate are shares of ALL WIA; dowRateOfArrivals is DOW as a share of those who arrived
 *   alive (the published definition, comparable with the US 2.5% and 4.56% figures)
 */
export const baseDowFromWounds = ({ woundDistribution, triageMix, t3Mortality, t4Mortality, arrivalHours, surgeryHours, surgeryHoursT1 = surgeryHours, t2ExtraWaitHours = 0, care }) => {
  if (!woundDistribution) throw new Error(`${WHERE}: woundDistribution is required`);
  if (!triageMix) throw new Error(`${WHERE}: triageMix is required`);
  ['t1', 't2', 't3', 't4'].forEach((k) => {
    if (typeof triageMix[k] !== 'number' || Number.isNaN(triageMix[k]) || triageMix[k] < 0) {
      throw new Error(`${WHERE}: triageMix.${k} must be a number >= 0 (got: ${triageMix[k]})`);
    }
  });
  [['t3Mortality', t3Mortality], ['t4Mortality', t4Mortality]].forEach(([n, v]) => {
    if (typeof v !== 'number' || Number.isNaN(v) || v < 0) throw new Error(`${WHERE}: ${n} must be a number >= 0 (got: ${v})`);
  });
  const total = WOUND_REGIONS_IN_DISTRIBUTION.reduce((s, r) => {
    const w = woundDistribution[r];
    if (typeof w !== 'number' || Number.isNaN(w) || w < 0) {
      throw new Error(`${WHERE}: woundDistribution.${r} must be a number >= 0 (got: ${w})`);
    }
    return s + w;
  }, 0);
  if (Math.abs(total - 1) > 0.02) throw new Error(`${WHERE}: woundDistribution shares must sum to 1 (got: ${total})`);

  const byRegion = {};
  let dowT1 = 0;
  let dowT2 = 0;
  let preT1 = 0;
  let preT2 = 0;
  WOUND_REGIONS_IN_DISTRIBUTION.forEach((r) => {
    const w = woundDistribution[r] / total;
    const arrivalT2 = arrivalHours + t2ExtraWaitHours;
    const surgeryT2 = surgeryHours + t2ExtraWaitHours;
    const d1 = dowShareOfCasualties(r, arrivalHours, surgeryHoursT1, care, woundDistribution, 'T1');
    const d2 = dowShareOfCasualties(r, arrivalT2, surgeryT2, care, woundDistribution, 'T2');
    const c1 = dowBetween(r, arrivalHours, surgeryHoursT1, care, woundDistribution, 'T1');
    const c2 = dowBetween(r, arrivalT2, surgeryT2, care, woundDistribution, 'T2');
    const p1 = prehospitalDeaths(r, arrivalHours, care, woundDistribution, 'T1');
    const p2 = prehospitalDeaths(r, arrivalT2, care, woundDistribution, 'T2');
    byRegion[r] = { weight: w, dow_t1: d1, dow_t2: d2, dow_of_arrivals_t1: c1, dow_of_arrivals_t2: c2, prehospital_t1: p1, prehospital_t2: p2 };
    dowT1 += w * d1;
    dowT2 += w * d2;
    preT1 += w * p1;
    preT2 += w * p2;
  });
  const dowRate = triageMix.t1 * dowT1 + triageMix.t2 * dowT2 + triageMix.t3 * t3Mortality + triageMix.t4 * t4Mortality;
  const prehospitalRate = triageMix.t1 * preT1 + triageMix.t2 * preT2;
  const dowRateOfArrivals = prehospitalRate < 1 ? dowRate / (1 - prehospitalRate) : 0;
  return { dowRate, prehospitalRate, dowRateOfArrivals, byRegion };
};
